/* =========================================================
  CBD HUT — theme runtime globals
  - Reads JSON blob from #cbh-theme-data
  - Assigns window.shopUrl / window.routes / strings
  - Adds shopify-design-mode class in Theme Editor
========================================================= */

(() => {
  const el = document.getElementById('cbh-theme-data');
  if (!el) return;

  let data = null;
  try {
    data = JSON.parse(el.textContent || '{}');
  } catch (e) {
    // Fail silently (do not break storefront)
    return;
  }

  // Expose a single namespace (nice for debugging)
  window.CBH_THEME = data;

  // Design mode helper
  if (window.Shopify && Shopify.designMode) {
    document.documentElement.classList.add('shopify-design-mode');
  }

  // Globals (only set if not already set)
  if (data.shopUrl && !window.shopUrl) window.shopUrl = data.shopUrl;
  if (data.routes && !window.routes) window.routes = data.routes;

  if (data.cartStrings && !window.cartStrings) window.cartStrings = data.cartStrings;
  if (data.variantStrings && !window.variantStrings) window.variantStrings = data.variantStrings;
  if (data.quickOrderListStrings && !window.quickOrderListStrings) window.quickOrderListStrings = data.quickOrderListStrings;
  if (data.accessibilityStrings && !window.accessibilityStrings) window.accessibilityStrings = data.accessibilityStrings;

// Optional: third-party initialisers controlled by JSON
// Lipscore: initialise on any page where widgets exist, and re-initialise after dynamic section updates.
const initLipscoreIfNeeded = () => {
  if (!data.lipscoreApiKey) return;
  const hasWidgets = document.querySelector('.lipscore-rating-small, #lipscore-rating, .lipscore-review-list');
  if (!hasWidgets) return;
  if (window.__CBH_LIPSCORE_READY__) {
    if (window.lipscore && typeof window.lipscore.init === 'function') {
      try { window.lipscore.init({ apiKey: data.lipscoreApiKey }); } catch (e) {}
    }
    return;
  }

  const doInit = () => {
    if (!window.lipscore || typeof window.lipscore.init !== 'function') return;
    try {
      window.__CBH_LIPSCORE_READY__ = true;
      window.lipscore.init({ apiKey: data.lipscoreApiKey });
      // Some Lipscore builds expose a render/refresh method; call if present.
      if (typeof window.lipscore.render === 'function') window.lipscore.render();
      if (typeof window.lipscore.refresh === 'function') window.lipscore.refresh();
    } catch (e) {
      // no-op
    }
  };

  // Expose for the Lipscore script to call if it wants
  window.lipscoreInit = doInit;

  const loadScript = () => {
    if (document.querySelector('script[data-cbh-lipscore]')) {
      doInit();
      return;
    }
    const locale = String(data.lipscoreLocale || 'en').toLowerCase();
    const scr = document.createElement('script');
    scr.async = true;
    scr.src = `https://static.lipscore.com/assets/${locale}/lipscore-v1.js`;
    scr.setAttribute('data-cbh-lipscore', '1');
    scr.onload = () => { doInit(); };
    document.head.appendChild(scr);
  };

  // Performance: load Lipscore only when a rating widget is near viewport or after user interaction.
  // This preserves functionality while reducing unused JS/CSS and main-thread work on initial load.
  const scheduleFallback = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadScript, { timeout: 4000 });
    } else {
      window.setTimeout(loadScript, 2500);
    }
  };

  const armInteraction = () => {
    const once = () => {
      window.removeEventListener('pointerdown', once, true);
      window.removeEventListener('keydown', once, true);
      loadScript();
    };
    window.addEventListener('pointerdown', once, { once: true, capture: true, passive: true });
    window.addEventListener('keydown', once, { once: true, capture: true });
  };

  if ('IntersectionObserver' in window && hasWidgets) {
    try {
      const io = new IntersectionObserver((entries, obs) => {
        if (entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
          obs.disconnect();
          loadScript();
        }
      }, { rootMargin: '300px 0px' });
      io.observe(hasWidgets);
      armInteraction();
      scheduleFallback();
    } catch (e) {
      armInteraction();
      scheduleFallback();
    }
  } else {
    armInteraction();
    scheduleFallback();
  }
};

// Run once on load
initLipscoreIfNeeded();

// Re-run after Shopify section reloads / theme editor changes
document.addEventListener('shopify:section:load', initLipscoreIfNeeded);
document.addEventListener('shopify:section:reorder', initLipscoreIfNeeded);
document.addEventListener('shopify:section:select', initLipscoreIfNeeded);

// Re-run when facets/pagination replace the grid (MutationObserver)
const mo = new MutationObserver((mutations) => {
  for (const m of mutations) {
    if (m.addedNodes && m.addedNodes.length) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && (node.matches('.lipscore-rating-small') || node.querySelector?.('.lipscore-rating-small'))) {
          initLipscoreIfNeeded();
          return;
        }
      }
    }
  }
});
mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
})();
